/**
 * What the terms panel says, and whether a typed proposal is fit to send.
 *
 * Pure. The server's two halves go in — the terms in force and every version
 * ever proposed — and a view comes out with every label decided. The copy is
 * `revisions.constants.ts`; the reads, the two writes and the form's state are
 * `useRevisionsController.ts`.
 *
 * THE ONE RULE THIS FILE HOLDS THAT THE SERVER ALSO HOLDS is "those are already
 * the terms". It is checked here as well, for `toOfferRequest`'s reason:
 * `ondo.propose_consignment_revision()` refuses it with a 22023 and that refusal
 * is the authority — this is the courtesy that saves the round trip and says it
 * in the reader's own words. Every other rule about the figures is the
 * database's alone.
 *
 * WHICH SIDE THE READER IS ON IS NOT DECIDED HERE AND CANNOT BE. It arrives as
 * `yoursToAnswer` and `yourSideOndoId`, resolved in SQL from the reader's
 * memberships — a party may be a firm the reader works for, and comparing the
 * reader's own ONDO id with the two on the row would answer no for every dealer
 * at every gallery.
 */

import type {
  ConsignmentBasis,
  ConsignmentRevision,
  ConsignmentRevisionsResponse,
  ConsignmentTerms,
  ProposeRevisionRequest,
} from "@ondo/shared";
import { formatMoney, isRevisionPending } from "@ondo/shared";

import { ApiError } from "../lib/api.ts";
import { formatConsignedDate } from "../gallery/gallery.model.ts";
import { parseMoneyMinor } from "../lib/money.ts";
import { REVISIONS_COPY } from "./revisions.constants.ts";

/** The typed form, as the fields hold it. Strings until submit. */
export interface RevisionDraft {
  basis: ConsignmentBasis;
  currency: string;
  asking: string;
  floor: string;
  note: string;
}

/** One label/value pair in a terms list. */
export interface TermLine {
  label: string;
  value: string;
}

/** One party's answer to one version, as a row of the agreement list. */
export interface AgreementLine {
  /** 'Gallery' or 'Owner', with '(you)' on whichever the reader acts for. */
  party: string;
  /** 'Accepted', 'Refused' or 'Not yet answered'. */
  answer: string;
  /** Whether this one is still outstanding, so the view can mark it. */
  outstanding: boolean;
}

/** The proposal awaiting an answer, when there is one. */
export interface PendingRevision {
  revisionId: string;
  /** 'Version 3', for the heading. */
  label: string;
  /** 'Proposed by Hauser & Wirth (ONDO-6624) on 04 Feb 2026'. */
  provenance: string;
  terms: TermLine[];
  agreement: AgreementLine[];
  /** Whether the reader's own side owes the answer. The server's verdict. */
  yours: boolean;
  /** The sentence under it, which differs by that. */
  note: string;
}

/** One settled version, as the history lists it. */
export interface SettledRevision {
  revisionId: string;
  label: string;
  provenance: string;
  /** 'Agreed', 'Refused', or the lapsed sentence. */
  outcome: string;
  terms: TermLine[];
}

export type RevisionsView =
  | { kind: "loading"; message: string }
  | { kind: "unavailable"; message: string }
  | {
      kind: "terms";
      /** The terms in force, with their version. */
      inForce: { label: string; since: string; terms: TermLine[] };
      pending: PendingRevision | null;
      history: SettledRevision[];
      /** Shown when nothing has ever been proposed. */
      historyNote: string | null;
      /**
       * Whether a new proposal may be started.
       *
       * FALSE WHILE ONE IS PENDING, which is not a guess about the server's
       * mood: `consignment_revisions_one_live` refuses a second live revision,
       * so a form offered here would meet a 409. One live proposal at a time is
       * what keeps "which version is in force" answerable.
       */
       canPropose: boolean;
    };

/**
 * What the panel shows, decided here.
 *
 * `canAct` draws the controls and nothing more — it is the account-level
 * capability, the same hint `ConsignPanel` takes, and whether THIS consignment
 * may be repriced by this caller is `ondo.propose_consignment_revision()`'s to
 * refuse. `yoursToAnswer` is different in kind: that one IS the server's answer
 * about this row, and the view obeys it.
 */
export function toRevisionsView(
  response: ConsignmentRevisionsResponse | undefined,
  canAct: boolean,
): RevisionsView {
  if (response === undefined) return { kind: "loading", message: REVISIONS_COPY.loading };
  if (!response.terms) return { kind: "unavailable", message: REVISIONS_COPY.unavailable };

  const pending = response.revisions.find(isRevisionPending);
  const settled = response.revisions.filter((each) => !isRevisionPending(each));

  return {
    kind: "terms",
    inForce: {
      label: versionLabel(response.terms.version),
      since: formatConsignedDate(response.terms.effectiveAt),
      terms: describeTerms(response.terms),
    },
    pending: pending ? toPending(pending) : null,
    history: settled.map(toSettled),
    historyNote: response.revisions.length === 0 ? REVISIONS_COPY.history.none : null,
    canPropose: canAct && !pending,
  };
}

/** 'As offered' for the original terms; 'Version 3' for a revision. */
export function versionLabel(version: number): string {
  return version === 0 ? REVISIONS_COPY.asOffered : REVISIONS_COPY.version(version);
}

/**
 * The four terms as lines.
 *
 * A FIGURE THAT WAS NEVER AGREED PRINTS A DASH AND NEVER A ZERO, which is
 * `money.ts`'s rule and matters more here than anywhere: a floor rendered as £0
 * on a consignment with no floor would read as "sell it for anything", which is
 * the opposite of what a floor is. The line is still shown, because its absence
 * is a term — `unspecified` is a real answer.
 */
export function describeTerms(terms: {
  basis: ConsignmentBasis;
  askingMinor: number | null;
  floorMinor: number | null;
  currency: string | null;
  note?: string | null;
}): TermLine[] {
  const lines: TermLine[] = [
    { label: REVISIONS_COPY.terms.basis, value: REVISIONS_COPY.basis[terms.basis] },
    { label: REVISIONS_COPY.terms.asking, value: figure(terms.askingMinor, terms.currency) },
    { label: REVISIONS_COPY.terms.floor, value: figure(terms.floorMinor, terms.currency) },
  ];

  if (terms.note) lines.push({ label: REVISIONS_COPY.terms.note, value: terms.note });
  return lines;
}

function figure(minor: number | null, currency: string | null): string {
  if (minor === null || !currency) return REVISIONS_COPY.noFigure;
  return formatMoney({ minor, currency });
}

/**
 * Each party's answer to one version.
 *
 * BOTH SIDES ALWAYS, including the one that has not answered, because "who has
 * agreed" is only legible beside who has not. OND-101 asks for exactly this:
 * "Show the old terms, the proposed terms, and each party's agreement."
 */
export function describeAgreement(revision: ConsignmentRevision): AgreementLine[] {
  return [
    line(
      REVISIONS_COPY.agreement.gallery,
      revision.galleryStance,
      revision.yourSideOndoId === revision.galleryOndoId,
    ),
    line(
      REVISIONS_COPY.agreement.owner,
      revision.ownerStance,
      revision.yourSideOndoId === revision.ownerOndoId,
    ),
  ];
}

function line(
  party: string,
  stance: ConsignmentRevision["galleryStance"],
  isYou: boolean,
): AgreementLine {
  const label = isYou ? `${party} (${REVISIONS_COPY.agreement.you})` : party;

  if (stance === "accept") {
    return { party: label, answer: REVISIONS_COPY.agreement.accepted, outstanding: false };
  }
  if (stance === "reject") {
    return { party: label, answer: REVISIONS_COPY.agreement.refused, outstanding: false };
  }
  return { party: label, answer: REVISIONS_COPY.agreement.awaiting, outstanding: true };
}

function toPending(revision: ConsignmentRevision): PendingRevision {
  return {
    revisionId: revision.revisionId,
    label: versionLabel(revision.version),
    provenance: provenanceOf(revision),
    terms: describeTerms(revision),
    agreement: describeAgreement(revision),
    yours: revision.yoursToAnswer,
    note: revision.yoursToAnswer
      ? REVISIONS_COPY.pending.yours
      : REVISIONS_COPY.pending.waiting,
  };
}

function toSettled(revision: ConsignmentRevision): SettledRevision {
  return {
    revisionId: revision.revisionId,
    label: versionLabel(revision.version),
    provenance: provenanceOf(revision),
    outcome: outcomeOf(revision),
    terms: describeTerms(revision),
  };
}

/**
 * 'Proposed by Hauser & Wirth (ONDO-6624) on 04 Feb 2026'.
 *
 * NAMES THE SIDE AND NOT THE PERSON, although the row carries both. The side is
 * the party to the agreement; which of its people pressed the button is a fact
 * about the answer rather than about the terms, and printing a junior's name
 * beside a price reads as though they had agreed it personally.
 */
function provenanceOf(revision: ConsignmentRevision): string {
  const side =
    revision.proposedForOndoId === revision.galleryOndoId
      ? `${revision.galleryName} (${revision.galleryOndoId})`
      : `${revision.ownerName} (${revision.ownerOndoId})`;

  return `${REVISIONS_COPY.pending.proposedBy} ${side} ${REVISIONS_COPY.pending.on} ${formatConsignedDate(revision.proposedAt)}`;
}

function outcomeOf(revision: ConsignmentRevision): string {
  if (revision.outcome === "accepted") return REVISIONS_COPY.history.accepted;
  if (revision.outcome === "rejected") return REVISIONS_COPY.history.rejected;
  return REVISIONS_COPY.history.lapsed;
}

/**
 * The draft, prefilled with the terms in force.
 *
 * PREFILLED RATHER THAN EMPTY, because a revision is a complete set of terms and
 * a blank form would make "change the floor" mean "agree no asking price". The
 * database takes the whole set either way — there is no partial update — so the
 * form has to start from what is in force or a reader would silently drop the
 * half they did not retype.
 */
export function draftFrom(terms: ConsignmentTerms): RevisionDraft {
  return {
    basis: terms.basis,
    currency: terms.currency ?? "GBP",
    asking: terms.askingMinor === null ? "" : String(terms.askingMinor / 100),
    floor: terms.floorMinor === null ? "" : String(terms.floorMinor / 100),
    note: "",
  };
}

/**
 * The draft as the server takes it, or the one problem that stops it.
 *
 * Figures are parsed here and never sent as text. Two refusals before the round
 * trip, and both have a reason beyond saving a request:
 *
 *   * A FLOOR ABOVE THE ASKING PRICE — `toOfferRequest`'s rule, carried over
 *     because the pair means the same thing here: the database would accept it,
 *     and a reader seeing "asking £100k, floor £150k" would reasonably assume
 *     the two were swapped.
 *   * TERMS THAT ARE ALREADY IN FORCE — the server refuses it too, and that
 *     refusal is the authority. Said here so the reader is told before pressing,
 *     in the one place that knows which figures they are looking at.
 */
export function toProposeRequest(
  draft: RevisionDraft,
  inForce: ConsignmentTerms,
): { ok: true; body: ProposeRevisionRequest } | { ok: false; problem: string } {
  const asking = draft.asking.trim() === "" ? null : parseMoneyMinor(draft.asking);
  const floor = draft.floor.trim() === "" ? null : parseMoneyMinor(draft.floor);

  if (draft.asking.trim() !== "" && asking === null) {
    return { ok: false, problem: REVISIONS_COPY.errors.invalidMoney };
  }
  if (draft.floor.trim() !== "" && floor === null) {
    return { ok: false, problem: REVISIONS_COPY.errors.invalidMoney };
  }
  if (asking !== null && floor !== null && floor > asking) {
    return { ok: false, problem: REVISIONS_COPY.errors.floorAboveAsking };
  }

  const currency = draft.currency.trim().toUpperCase();
  if ((asking !== null || floor !== null) && currency.length !== 3) {
    return { ok: false, problem: REVISIONS_COPY.errors.currencyNeeded };
  }

  const note = draft.note.trim();
  const body: ProposeRevisionRequest = {
    basis: draft.basis,
    askingMinor: asking,
    floorMinor: floor,
    // Null when there are no figures to name a currency for, so a consignment
    // repriced to "no price agreed" does not carry a stray code.
    currency: asking === null && floor === null ? null : currency,
    note: note === "" ? null : note,
  };

  if (
    body.basis === inForce.basis &&
    asking === inForce.askingMinor &&
    floor === inForce.floorMinor &&
    body.currency === inForce.currency
  ) {
    return { ok: false, problem: REVISIONS_COPY.errors.unchanged };
  }

  return { ok: true, body };
}

/**
 * A refusal, in the reader's words.
 *
 * `describeApiError` is deliberately not used for the two codes this feature
 * means something specific by: a 409 here is always the one live proposal, and a
 * 403 is always "not your consignment to reprice". Everything else falls through
 * to the server's own sentence, which is the rule every screen keeps.
 */
export function describeRevisionError(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.status === 409) return REVISIONS_COPY.errors.alreadyPending;
    if (cause.status === 403) return REVISIONS_COPY.errors.refused;
    if (cause.status === 404) return REVISIONS_COPY.errors.gone;
    if (cause.message) return cause.message;
  }
  return REVISIONS_COPY.errors.refused;
}
